// eslint-disable-next-line no-restricted-imports
import type { Maybe } from '__generated__/graphql-types';

import type {
  PageConfig_GroupedComponentsWrapperMetadataFragment as GroupedComponentsWrapper,
  PageConfigComponentsQuery,
} from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import type { PageConfigFetchMoreFunction } from 'bundles/page-config-common/types/pagination';

export const pageConfigFetchMoreUpdateQuery = (
  previousData: PageConfigComponentsQuery,
  fetchMoreResult?: PageConfigComponentsQuery
) => {
  if (!fetchMoreResult) {
    return previousData;
  }

  const previousElements = previousData?.PageConfig?.queryPaginatedComponents?.elements;
  const fetchMoreElements = fetchMoreResult?.PageConfig?.queryPaginatedComponents?.elements;
  const mergedElements: typeof previousElements = [];
  const groupedComponentsWrappersAdded: { [key: string]: boolean } = {};

  // Merge elements from previous data and fetch more results.
  // Notably, we need to ensure we don't render duplicate grouped components wrappers when we are recieving partial data from the fetchMoreResults.
  // This populates a new array to represent the merged results while being mindful of merging nested components in grouped component wrappers across previous/next results.
  previousElements?.forEach((element) => {
    if (!element) {
      return;
    }

    if (element.__typename === 'PageConfig_GroupedComponentsWrapper') {
      groupedComponentsWrappersAdded[element.id] = true;
      const sameGroupedWrapperFromFetchMore = fetchMoreElements?.find((fetchMoreElement) => {
        if (!fetchMoreElement) {
          return false;
        }

        return (
          fetchMoreElement.__typename === 'PageConfig_GroupedComponentsWrapper' && fetchMoreElement.id === element.id
        );
      }) as GroupedComponentsWrapper | undefined;

      if (sameGroupedWrapperFromFetchMore) {
        mergedElements.push({
          ...element,
          components: element.components.concat(sameGroupedWrapperFromFetchMore?.components),
        });
      } else {
        mergedElements.push(element);
      }
    } else {
      mergedElements.push(element);
    }
  });
  fetchMoreElements?.forEach((element) => {
    if (!element) {
      return;
    }

    if (element.__typename === 'PageConfig_GroupedComponentsWrapper') {
      // Don't add grouped components wrappers that were added when iterating through previousData.
      // In the previous iteration, we find the same grouped component wrapper from fetchMoreResults and merge the components there.
      if (!groupedComponentsWrappersAdded[element.id]) {
        mergedElements.push(element);
      }
    } else {
      mergedElements.push(element);
    }
  });

  return {
    ...previousData,
    PageConfig: {
      queryPaginatedComponents: {
        elements: mergedElements,
        pagination: fetchMoreResult?.PageConfig?.queryPaginatedComponents?.pagination || { cursor: null },
        componentListMetadataHash:
          fetchMoreResult?.PageConfig?.queryPaginatedComponents?.componentListMetadataHash ||
          previousData?.PageConfig?.queryPaginatedComponents?.componentListMetadataHash ||
          '',
      },
    },
  } as PageConfigComponentsQuery;
};

// Handles pagination functionality for page config components query with a show more/infinite scroll implementation.
// Results get merged with existing data.
const pageConfigFetchMore = (
  limit: number,
  fetchMore?: PageConfigFetchMoreFunction,
  pagination?: {
    cursor?: Maybe<string>;
    componentListMetadataHash?: string;
  }
) => {
  fetchMore?.({
    variables: {
      cursor: pagination?.cursor,
      // This hash is how we keep track of the current "session" of pagination
      // as config changes/overrides in the BE could potentially change the list of components, which could disrupt paginated queries.
      // This must be passed in to ensure that the query is consistent across multiple fetchMore calls.
      componentListMetadataHash: pagination?.componentListMetadataHash,
      limit,
    },
    updateQuery: (previousData, { fetchMoreResult }) => {
      return pageConfigFetchMoreUpdateQuery(previousData, fetchMoreResult);
    },
  });
};

export default pageConfigFetchMore;
